import { Form } from "@circle-react-uikit/Form";
import {
  useReportCommentMutation,
  type ReportFormValues,
} from "@circle-react/components/CommentsV3/hooks/useReportCommentMutation";
import { Modal } from "@circleco/compass/components/Modal";

import { t } from "@/i18n-js/instance";

import type { ReportModalProps } from "./types";

const FORM_ID = "report-comment-modal-form";

const REPORT_REASONS = [
  "harassment",
  "spam",
  "incorrect_location",
  "against_guidelines",
  "other",
] as const;

interface ReportModalComponentProps extends ReportModalProps {
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

export const ReportModalComponent = ({
  commentId,
  open,
  onOpenChange,
}: ReportModalComponentProps) => {
  const { mutate: submitReport, isPending: isReporting } =
    useReportCommentMutation({
      commentId,
      onSuccess: () => onOpenChange(false),
    });

  const reportReasonOptions = REPORT_REASONS.map(reason => ({
    value: reason,
    label: t(`flagged_contents.report_reasons.${reason}`),
  }));

  const handleSubmit = (data: ReportFormValues) => {
    submitReport(data);
  };

  return (
    <Modal.Root open={open} onOpenChange={onOpenChange} size="md">
      <Modal.Content>
        <Modal.Header title={t("comments.report.form.title")} />
        <Modal.Body>
          <Form<ReportFormValues>
            id={FORM_ID}
            defaultValues={{
              reported_reason_body: "",
              reported_reason_type: "",
            }}
            onSubmit={handleSubmit}
          >
            <Form.Item
              description={t(
                "flagged_contents.form.reported_reason_type_description",
              )}
              hideBorder
              isFirstChild
              label={t("flagged_contents.form.reported_reason_type")}
              name="reported_reason_type"
              rules={{
                required: t("settings.validation.this_field_is_required"),
              }}
            >
              <Form.SelectNative
                name="reported_reason_type"
                options={reportReasonOptions}
                prompt={t("flagged_contents.form.reported_reason_type_select")}
              />
            </Form.Item>
            <Form.Item
              description={t(
                "flagged_contents.form.reported_reason_body_description",
              )}
              fullWidth
              hideBorder
              label={t("flagged_contents.form.reported_reason_body")}
              name="reported_reason_body"
              placeholder={t(
                "flagged_contents.form.reported_reason_body_placeholder",
              )}
            >
              <Form.TextArea name="reported_reason_body" />
            </Form.Item>
          </Form>
        </Modal.Body>
        <Modal.Footer
          secondaryAction={{
            label: t("cancel"),
            onClick: () => onOpenChange(false),
          }}
          primaryAction={{
            label: t("report"),
            type: "submit",
            form: FORM_ID,
            loading: isReporting,
            disabled: isReporting,
          }}
        />
      </Modal.Content>
    </Modal.Root>
  );
};
